import { CssVarsProvider, styled } from '@mui/joy';
import useMaintenanceWindowHook from './hook/useMaintenanceWindowHook';
import {
	Box,
	Card,
	Grid,
	Sheet,
	Typography,
} from '@farmart-engineering/central-component-library';
import { FeatureReleasePlan } from '@farmart-engineering/brave-ship-react';

// Styled components for special effects
const StickyNote = styled(Card)(() => ({
	background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
	position: 'relative',
	maxWidth: '240px',
	transform: 'rotate(-1deg)',
	transition: 'all 0.3s ease',
	border: '1px solid #e9ecef',
	boxShadow:
		'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
	'&:hover': {
		transform: 'rotate(0deg) scale(1.02)',
		boxShadow:
			'0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
	},
	'&::before': {
		content: '""',
		position: 'absolute',
		top: '-6px',
		left: '50%',
		transform: 'translateX(-50%)',
		width: '40px',
		height: '16px',
		backgroundColor: '#f1f3f4',
		opacity: 0.8,
		borderRadius: '3px',
		boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
		border: '1px solid #e9ecef',
		zIndex: 1,
	},
	'&::after': {
		content: '""',
		position: 'absolute',
		bottom: '-2px',
		right: '-2px',
		width: '16px',
		height: '16px',
		backgroundColor: '#f8f9fa',
		transform: 'rotate(45deg)',
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
		border: '1px solid #e9ecef',
		borderLeft: 'none',
		borderTop: 'none',
		zIndex: 0,
	},
}));

const StickyNoteShadow = styled(Box)({
	position: 'absolute',
	inset: 0,
	backgroundColor: 'rgba(0,0,0,0.1)',
	transform: 'translate(4px, 4px) rotate(-1deg)',
	zIndex: -1,
	filter: 'blur(6px)',
	borderRadius: '8px',
});

export default function MaintenanceWindow(props: FeatureReleasePlan) {
	const { timeLeft } = useMaintenanceWindowHook({
		downtimeEndAt: props.downtimeEndAt,
		downtimeStartAt: props.downtimeStartAt,
	});
	return (
		<CssVarsProvider>
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					p: 2,
					background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
					position: 'relative',
					overflow: 'hidden',
					backgroundImage:
						'url(https://d1ml6e9fwr2kf5.cloudfront.net/os/payment_docs/2025/5/27/71d75e464d5448439d899e995083b007_maintenance_bgd_720.jpg.jpg)',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<Sheet
					variant='outlined'
					sx={{
						maxWidth: '900px',
						width: '100%',
						overflow: 'hidden',
						borderRadius: '12px',
						boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
						bgcolor: '#ffffff',
						border: '1px solid #e9ecef',
						position: 'relative',
						zIndex: 1,
					}}
				>
					<Grid container spacing={0} minHeight={{ xs: '80vh', md: '50vh' }}>
						<Grid
							xs={12}
							md={5}
							sx={{
								p: { xs: 3, md: 4 },
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								bgcolor: '#f8f9fa',
							}}
						>
							{/* Direct Image without AspectRatio */}
							<Box
								sx={{
									position: 'relative',
									width: '240px',
									height: '240px',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								<img
									src='https://d1ml6e9fwr2kf5.cloudfront.net/os/payment_docs/2025/5/23/adee637981db4d55b0d3a77f500382f3_favpng_c706cb82f4e7484a2ba74d11e602dc24.png.png'
									alt='Maintenance illustration'
									width={360}
									height={360}
									style={{
										objectFit: 'contain',
										mixBlendMode: 'multiply',
										filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
									}}
								/>
							</Box>
						</Grid>

						<Grid
							xs={12}
							md={7}
							sx={{
								p: { xs: 3, md: 4 },
								textAlign: { xs: 'center', md: 'left' },
								bgcolor: '#f8f9fa',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
							}}
						>
							<Typography
								level='h1'
								sx={{
									mb: 2,
									color: '#343a40',
									fontWeight: 700,
									fontSize: { xs: '1.75rem', md: '2rem' },
									lineHeight: 1.2,
								}}
							>
								Maintenance Break!
							</Typography>
							<Typography fontWeight={700}>
								{`Release - ${props?.releaseVersion}`}{' '}
							</Typography>

							<Typography
								level='body-lg'
								sx={{
									mb: 3,
									color: '#6c757d',
									lineHeight: 1.5,
									fontSize: '1rem',
								}}
							>
								{props?.generalNote ??
									'Our site is currently undergoing scheduled maintenance. Please check the release notes to see the upcoming improvements.'}
								<br />
								<br />
								Thank you for your patience!
							</Typography>

							{/* Sticky Note */}
							<Box
								sx={{
									position: 'relative',
									display: 'flex',
									justifyContent: { xs: 'center', md: 'flex-start' },
									mb: 2,
								}}
							>
								<Box sx={{ position: 'relative' }}>
									{props?.releaseNotes && props?.releaseNotes?.length !== 0 && (
										<StickyNote sx={{ p: 2, zIndex: 2 }}>
											<Box sx={{ position: 'relative', zIndex: 3 }}>
												<Typography
													level='title-md'
													sx={{
														mb: 1.5,
														fontWeight: 700,
														color: '#495057',
														fontSize: '0.9rem',
													}}
												>
													🎉 After Launch:
												</Typography>
												{props?.releaseNotes?.map((note: string) => (
													<Typography
														level='body-sm'
														sx={{
															mb: 0.5,
															color: '#6c757d',
															fontSize: '0.8rem',
														}}
													>
														✓ {note}
													</Typography>
												))}
												<Typography
													level='body-sm'
													sx={{
														mt: 1,
														fontStyle: 'italic',
														color: '#868e96',
														fontSize: '0.75rem',
													}}
												>
													Be the first to experience something amazing!
												</Typography>
											</Box>
										</StickyNote>
									)}
									<StickyNoteShadow />
								</Box>
							</Box>
						</Grid>
					</Grid>

					{/* Countdown Section */}
					<Box
						sx={{
							bgcolor: '#e9ecef',
							p: { xs: 2, md: 3 },
							textAlign: 'center',
							borderTop: '1px solid #dee2e6',
						}}
					>
						<Typography
							level='body-sm'
							sx={{
								mb: 1.5,
								color: '#868e96',
								textTransform: 'uppercase',
								letterSpacing: '0.15em',
								fontWeight: 600,
								fontSize: '0.7rem',
							}}
						>
							WE WILL BE LIVE IN
						</Typography>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								gap: 3,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
								}}
							>
								<Typography
									level='h1'
									sx={{
										fontWeight: 700,
										color: '#495057',
										fontSize: { xs: '2rem', md: '2.5rem' },
										lineHeight: 1,
									}}
								>
									{timeLeft.hours}
								</Typography>
								<Typography
									level='body-sm'
									sx={{
										color: '#868e96',
										mt: 0.5,
										fontWeight: 600,
										letterSpacing: '0.1em',
										fontSize: '0.7rem',
									}}
								>
									HOURS
								</Typography>
							</Box>

							<Typography
								level='h2'
								sx={{
									color: '#495057',
									pb: 2,
									fontSize: { xs: '1.5rem', md: '2rem' },
									fontWeight: 300,
								}}
							>
								:
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
								}}
							>
								<Typography
									level='h1'
									sx={{
										fontWeight: 700,
										color: '#495057',
										fontSize: { xs: '2rem', md: '2.5rem' },
										lineHeight: 1,
									}}
								>
									{timeLeft.minutes}
								</Typography>
								<Typography
									level='body-sm'
									sx={{
										color: '#868e96',
										mt: 0.5,
										fontWeight: 600,
										letterSpacing: '0.1em',
										fontSize: '0.7rem',
									}}
								>
									MINUTES
								</Typography>
							</Box>
							<Typography
								level='h2'
								sx={{
									color: '#495057',
									pb: 2,
									fontSize: { xs: '1.5rem', md: '2rem' },
									fontWeight: 300,
								}}
							>
								:
							</Typography>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
								}}
							>
								<Typography
									level='h1'
									sx={{
										fontWeight: 700,
										color: '#495057',
										fontSize: { xs: '2rem', md: '2.5rem' },
										lineHeight: 1,
									}}
								>
									{timeLeft.seconds}
								</Typography>
								<Typography
									level='body-sm'
									sx={{
										color: '#868e96',
										mt: 0.5,
										fontWeight: 600,
										letterSpacing: '0.1em',
										fontSize: '0.7rem',
									}}
								>
									SECONDS
								</Typography>
							</Box>
						</Box>
					</Box>
				</Sheet>
			</Box>
		</CssVarsProvider>
	);
}
